import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navigation from './Navigation';

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="terms-page">
      <Navigation 
        showDemoButton={false}
        secondaryButtonText="Back to Home"
        onSecondaryClick={() => navigate('/')}
        trackingLocation="terms_page"
      />
      <div className="terms-container">
        <div className="terms-header">
          <h1>Terms of Service</h1>
          <p><strong>Effective Date:</strong> October 8, 2025</p>
        </div>
        
        <div className="terms-content">
          <section className="terms-section">
            <h2>1. Overview</h2>
            <p>
              These Terms of Service (“Terms”) govern your use of the Solidly Finance desktop application (“the App”), owned and operated by SolidlyAI, a doing-business-as (DBA) of Alek Hagopian Software & Services LLC (“we,” “us,” or “our”). By installing or using the App, you agree to these Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. License</h2>
            <p>
              Upon purchase, we grant you a <strong>perpetual, non-exclusive, non-transferable license</strong> to install and use Solidly Finance on your personal computer.
            </p>
            <ul>
              <li>You may install the App on up to three devices that you personally own.</li>
              <li>You may not transfer your license to another person. The license only entitles a single person to the App.</li>
              <li>You may not share, distribute, or resell the App without written permission from SolidlyAI.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>3. Ownership</h2>
            <p>
              The App and all associated intellectual property remain the exclusive property of SolidlyAI. Purchasing a license grants you usage rights, not ownership of the underlying code, design, or trademarks.
            </p>
          </section>

          <section className="terms-section">
            <h2>4. User Responsibilities</h2>
            <p>You are solely responsible for:</p>
            <ul>
              <li>Protecting access to your device and password</li>
              <li>Backing up your local data as needed</li>
              <li>Using the App in compliance with applicable laws</li>
            </ul>
            <p>
              SolidlyAI is not responsible for lost or corrupted data due to device failure, forgotten passwords, or accidental deletion.
            </p>
          </section>

          <section className="terms-section">
            <h2>5. No Financial, Investment, or Legal Advice</h2>
            <p>
              Solidly Finance is a financial planning and analysis tool intended for informational and educational purposes only. The App and any outputs, insights, or recommendations it provides are not intended to constitute financial, investment, tax, or legal advice, and should not be relied upon as such.
            </p>
            <p>
              You are solely responsible for evaluating your own financial situation and making your own decisions. We do not guarantee any particular financial outcome or performance result based on your use of the App.
            </p>
            <p>
              By using Solidly Finance, you acknowledge and agree that:
            </p>
            <ul>
              <li>The App does not provide personalized investment or financial advice tailored to your circumstances.</li>
              <li>We are not liable for any losses, damages, or decisions made based on information or insights generated by the App.</li>
              <li>You should consult with a qualified financial advisor, accountant, or attorney before making any financial decisions.</li>
            </ul>
            <p>
              Your use of the App is at your own risk. SolidlyAI assumes no responsibility or liability for your financial outcomes, investment performance, or any consequences arising from your use or reliance on the App.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Security</h2>
            <p>
              Your financial data is encrypted and stored locally. We never access or transmit it. While we take steps to ensure the App's integrity, you are responsible for maintaining a secure computing environment and keeping your copy of the App updated.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Updates and Support</h2>
            <p>
              We may release updates or improvements to the App from time to time. These updates are included at no additional cost.
            </p>
            <p>
              Support is provided on a best-effort basis via <a href="mailto:support@solidly.ai">support@solidly.ai</a>. We do not guarantee response times or specific resolutions.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Payment and Refunds</h2>
            <p>
              Solidly Finance is a <strong>one-time purchase</strong> product. All payments are handled through our third-party payment provider.
            </p>
            <p>
              <strong>7-Day Money-Back Guarantee:</strong> We offer a 7-day refund period from the date of purchase. If you are not satisfied with the App for any reason, you may request a full refund within 7 days by contacting us at <a href="mailto:support@solidly.ai">support@solidly.ai</a>.
            </p>
            <p>
              Upon receiving your refund request, we will process your refund and revoke your license to use the App. You must cease using the App and uninstall it from all devices. Continued use of the App after a refund has been issued constitutes a violation of these Terms.
            </p>
            <p>
              Refund requests made after the 7-day period will be evaluated on a case-by-case basis at our sole discretion.
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Disclaimer of Warranties</h2>
            <p>
              The App is provided "as is," without warranties of any kind, express or implied. We do not guarantee that the App will meet your requirements or operate without interruption or errors.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, SolidlyAI and its affiliates are not liable for any indirect, incidental, consequential, or special damages, including loss of data or profits, arising out of your use or inability to use the App.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Termination</h2>
            <p>
              You may stop using the App at any time. We may terminate your license if you violate these Terms, such as by redistributing or reverse-engineering the software.
            </p>
          </section>

          <section className="terms-section">
            <h2>12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the Commonwealth of Virginia, United States, without regard to conflict of law principles.
            </p>
          </section>

          <section className="terms-section">
            <h2>13. Contact</h2>
            <p>For questions about these Terms, please contact:</p>
            <div className="contact-info">
              <p><strong>SolidlyAI</strong></p>
              <p>Email: <a href="mailto:support@solidly.ai">support@solidly.ai</a></p>
              <p>Website: <a href="https://get.solidly.ai">get.solidly.ai</a></p>
            </div>
          </section>
        </div>
        
        <div className="back-to-home">
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;